import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { getScacCodes, submitTrackingReferences, updateTrackingReferences } from "api";
import { useGetTrackingReferences } from "api/queries/shipments/useGetTrackingReferences";
import Button from "components/v2/Button/Button";
import Input from "components/v2/Input/Input";
import Modal from "components/v2/Modal/Modal";
import BaseSelect from "components/v2/Select/BaseSelect";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import validateTrackingNumber from "helpers/trackingNumberValidator";
import { useTrackingStopAssociation } from "pages/shipper/ShipmentTracking/components/Tracking/components/ReferencesDialog/useTrackingStopAssociation";
import { ReactComponent as InfoIcon } from "assets/images/icons/Info.svg";

import detectTrackingType, { shouldRedetectTrackingType } from "./detectTrackingType";
import getAllowedTrackingTypes from "./getAllowedTrackingTypes";
import { resolvePastedReference } from "./resolvePastedReference";
import TrackingScacSelect from "./TrackingScacSelect";
import TrackingStopAssociationSelect from "./TrackingStopAssociationSelect";
import {
  findScacNumberInList,
  isValidScacCode,
  resolveAutoContainerScacNumber,
  resolveScacName,
  resolveScacNumberFromStoredName,
  SCAC_OTHER_VALUE,
  ScacCode,
} from "./trackingScacUtils";

type TrackingTypeOption = { label: string; value: string };

type TrackingReference = {
  id: number;
  type: string;
  tracking_number: string;
  scac?: string;
  bol?: string;
  stop?: { id: number };
};

type AddTrackingNumberModalProps = {
  isOpen: boolean;
  close: () => void;
  typeOptions: TrackingTypeOption[];
  prevReferences: TrackingReference[];
  refreshTrackingData: (type: string) => void;
  shipmentId: string;
  referenceId?: number | null;
  freightMode: string;
  freightModeType?: string;
  company_shipper_settings?: { truck_tracking_enabled?: boolean };
};

const unsupportedLiveTrackingTypes = new Set(["rail_car"]);

const AddTrackingNumberModal: FC<AddTrackingNumberModalProps> = function ({
  isOpen,
  close,
  typeOptions,
  prevReferences,
  refreshTrackingData,
  shipmentId,
  referenceId = null,
  freightMode,
  freightModeType,
  company_shipper_settings = {},
}) {
  const intl = useIntl();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reference, setReference] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [typeManuallySet, setTypeManuallySet] = useState(false);
  const [associatedStopId, setAssociatedStopId] = useState<number | null>(null);
  const [selectedScacNumber, setSelectedScacNumber] = useState<string | null>(null);
  const [scacOtherCode, setScacOtherCode] = useState("");
  const [containerBol, setContainerBol] = useState("");
  const [validationError, setValidationError] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [scacCodes, setScacCodes] = useState<ScacCode[]>([]);
  const hasRequestedScacCodes = useRef(false);

  const { isEnabled: canAssociateStop, stops: deliveryStops } = useTrackingStopAssociation(shipmentId);

  const { data: trackingReferencesData } = useGetTrackingReferences(shipmentId, {
    enabled: !!shipmentId,
    refetchOnMount: false,
  });
  const { references = [] } = trackingReferencesData ?? {};

  const allowedOptions = useMemo(() => {
    let options = getAllowedTrackingTypes(freightMode, freightModeType, typeOptions, {
      includeHbl: true,
      excludeBookingReference: true,
    });

    if (!referenceId) {
      const primaryReference = prevReferences?.find((r) => r.type === "primary");
      if (primaryReference) {
        options = options.filter((option) => option.value !== "primary");
      }
    }

    return options;
  }, [freightMode, freightModeType, typeOptions, prevReferences, referenceId]);

  const allowedTypeValues = useMemo(() => allowedOptions.map((option) => option.value), [allowedOptions]);

  const selectedTypeOption = useMemo(
    () => typeOptions.find((option) => option.value === selectedType) ?? allowedOptions.find((option) => option.value === selectedType),
    [allowedOptions, selectedType, typeOptions],
  );

  const typeInformation = useMemo(() => validateTrackingNumber(selectedType), [selectedType]);

  const mbolReferenceHint =
    selectedType === "mbol"
      ? intl.formatMessage({
          id: "tracking.mbolReferenceHelp",
          defaultMessage:
            "Bill number only (7-10 digits). Select the SCAC code below. Pasting a combined value (e.g. TCLU7102609) moves the carrier code to SCAC and keeps the digits here.",
        })
      : undefined;

  const resetFormState = () => {
    setReference("");
    setSelectedType("");
    setTypeManuallySet(false);
    setAssociatedStopId(null);
    setSelectedScacNumber(null);
    setScacOtherCode("");
    setContainerBol("");
    setValidationError(false);
    setValidationErrorMessage("");
  };

  useEffect(() => {
    if (!isOpen || hasRequestedScacCodes.current) return;

    hasRequestedScacCodes.current = true;
    getScacCodes()
      .then((res) => {
        setScacCodes(res.data);
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveScacCodes",
            defaultMessage: "Could not retrieve SCAC codes. Please try again later.",
          }),
        ),
      );
  }, [isOpen, intl]);

  useEffect(() => {
    if (!isOpen || referenceId) return;
    resetFormState();
  }, [referenceId, isOpen]);

  useEffect(() => {
    if (!isOpen || !referenceId) return;

    const existingReference = prevReferences.find((r) => r.id === referenceId);
    setAssociatedStopId(existingReference?.stop?.id ?? null);

    if (!existingReference?.type) return;

    setSelectedType(existingReference.type);
    setTypeManuallySet(true);

    if (existingReference.type === "mbol") {
      const refSplit = existingReference.tracking_number.split("||");
      const scacPrefix = refSplit[0];
      const scacTrackingNumber = refSplit[1];
      const scacMatch = scacCodes.find((code) => code.number.toLowerCase() === scacPrefix.toLowerCase());
      setReference(scacTrackingNumber);
      if (scacMatch) {
        setSelectedScacNumber(scacMatch.number);
      } else {
        setSelectedScacNumber(SCAC_OTHER_VALUE);
        setScacOtherCode(scacPrefix);
      }
      return;
    }

    if (existingReference.type === "container_no") {
      setReference(existingReference.tracking_number);
      setContainerBol(existingReference.bol ?? "");
      if (existingReference.scac) {
        setSelectedScacNumber(resolveScacNumberFromStoredName(existingReference.scac, scacCodes));
      }
      return;
    }

    setReference(existingReference.tracking_number);
  }, [referenceId, isOpen, prevReferences, scacCodes]);

  useEffect(() => {
    if (selectedType !== "container_no") return;

    setSelectedScacNumber(resolveAutoContainerScacNumber(reference, scacCodes));
  }, [reference, selectedType, scacCodes]);

  useEffect(() => {
    if (selectedType !== "mbol" || selectedScacNumber !== SCAC_OTHER_VALUE || !scacOtherCode) return;

    const matchedScacNumber = findScacNumberInList(scacOtherCode, scacCodes);
    if (!matchedScacNumber) return;

    setSelectedScacNumber(matchedScacNumber);
    setScacOtherCode("");
  }, [reference, scacCodes, scacOtherCode, selectedScacNumber, selectedType]);

  useEffect(() => {
    setValidationError(false);
    setValidationErrorMessage("");
  }, [selectedType]);

  useEffect(() => {
    validateInput();
  }, [reference, selectedType, selectedScacNumber, scacOtherCode, references, typeInformation, referenceId, intl]);

  const handleReferenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;
    const shouldRedetect = typeManuallySet && shouldRedetectTrackingType(value, selectedType);
    const useAutoDetect = !typeManuallySet || shouldRedetect;
    const nextType = useAutoDetect ? detectTrackingType(value, allowedTypeValues) : selectedType;
    if (["rail_car", "container_no"].includes(nextType)) {
      value = value.toUpperCase();
    }
    setReference(value);

    if (useAutoDetect) {
      setSelectedType(nextType);
      if (shouldRedetect) {
        setTypeManuallySet(false);
      }
    }
  };

  const handleTypeChange = (value: string | null) => {
    if (!value) return;
    setSelectedType(value);
    setTypeManuallySet(true);
  };

  const onTrackingNumberPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = event.clipboardData.getData("text");
    if (!pastedValue.trim()) return;

    event.preventDefault();

    const resolved = resolvePastedReference(
      pastedValue,
      allowedTypeValues,
      scacCodes,
      selectedType,
      typeManuallySet,
    );

    setReference(resolved.reference);
    setSelectedType(resolved.selectedType);
    setTypeManuallySet(resolved.typeManuallySet);
    setSelectedScacNumber(resolved.selectedScacNumber);
    setScacOtherCode(resolved.scacOtherCode);
  };

  const generateReferenceValue = () => {
    if (!reference) return reference;

    if (selectedType === "mbol" && selectedScacNumber) {
      if (selectedScacNumber === SCAC_OTHER_VALUE) return `${scacOtherCode}||${reference}`;
      return `${selectedScacNumber}||${reference}`;
    }

    return reference;
  };

  const validateInput = () => {
    const normalizedReference = generateReferenceValue();
    const duplicatedReference = references.find(
      (ref) => ref.tracking_number === normalizedReference && ref.id !== referenceId,
    );
    if (duplicatedReference) {
      setValidationError(true);
      if (duplicatedReference.type === "container_no") {
        setValidationErrorMessage(
          intl.formatMessage({
            id: "tracking.duplicateContainerNumber",
            defaultMessage: "Container number already added.",
          }),
        );
      } else if (duplicatedReference.type === "mawb") {
        setValidationErrorMessage(
          intl.formatMessage({
            id: "tracking.duplicateMawbNumber",
            defaultMessage: "MAWB number already added.",
          }),
        );
      } else {
        setValidationErrorMessage(
          intl.formatMessage({
            id: "tracking.duplicateTrackingNumber",
            defaultMessage: "Tracking number already added.",
          }),
        );
      }
      return;
    }

    setValidationErrorMessage("");

    if (!typeInformation?.validationPattern) {
      setValidationError(false);
      return;
    }

    const regex = new RegExp(typeInformation.validationPattern);
    if (reference && !regex.test(reference)) {
      setValidationError(true);
      return;
    }

    setValidationError(false);
  };

  const isButtonDisabled = () => {
    if (referenceId && !reference) return false;
    const isScacCodeEmpty = selectedType === "mbol" && !selectedScacNumber;
    const isScacOtherCodeInvalid =
      selectedScacNumber === SCAC_OTHER_VALUE && !isValidScacCode(scacOtherCode);
    return !selectedType || validationError || !reference || isScacCodeEmpty || isScacOtherCodeInvalid;
  };

  const onSave = async () => {
    if (validationError || isButtonDisabled()) return;

    setIsSubmitting(true);
    const containerScacName = resolveScacName(selectedScacNumber, scacCodes);
    const trackingPayload = {
      type: selectedType,
      tracking_number: generateReferenceValue(),
      ...(selectedType === "container_no" &&
        containerScacName && {
          scac: containerScacName,
        }),
      ...(selectedType === "container_no" &&
        containerBol && {
          bol: containerBol,
        }),
      ...(canAssociateStop && { stop_id: associatedStopId }),
    };

    try {
      if (referenceId) {
        await updateTrackingReferences(shipmentId, {
          references: [{ id: referenceId, ...trackingPayload }],
        });
      } else {
        await submitTrackingReferences(shipmentId, {
          references: [trackingPayload],
        });
      }
      refreshTrackingData(selectedType);
      close();
    } catch (err) {
      errorToast(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle = referenceId
    ? intl.formatMessage({ id: "tracking.editReference", defaultMessage: "Edit reference" })
    : intl.formatMessage({ id: "tracking.addTrackingNumber", defaultMessage: "Add tracking number" });

  return (
    <Modal show={isOpen} onHide={close} size="tracking">
      <Modal.Header>
        <div className="text-primary text-xl font-semibold">{modalTitle}</div>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column mt-2xl">
          <Input
            label={intl.formatMessage({ id: "tracking.reference", defaultMessage: "Reference" })}
            required
            value={reference}
            onChange={handleReferenceChange}
            onPaste={onTrackingNumberPaste}
            placeholder={typeInformation?.placeholder ?? "Please provide"}
            hintText={mbolReferenceHint}
            hasError={validationError}
            errorMessage={validationErrorMessage}
            testId="add-tracking-number-reference-input"
          />

          {referenceId ? (
            selectedTypeOption && <p className="mt-3 mb-0 font-b3">{selectedTypeOption.label}</p>
          ) : (
            <div className="mt-3">
              <BaseSelect
                label={intl.formatMessage({ id: "tracking.type", defaultMessage: "Type" })}
                options={allowedOptions}
                value={selectedType || null}
                onChange={handleTypeChange}
                testId="add-tracking-number-type-select"
                isSearchable={false}
              />
            </div>
          )}

          {canAssociateStop && (
            <TrackingStopAssociationSelect stops={deliveryStops} value={associatedStopId} onChange={setAssociatedStopId} />
          )}

          {selectedType === "mbol" && (
            <>
              <TrackingScacSelect
                scacCodes={scacCodes}
                value={selectedScacNumber}
                onChange={setSelectedScacNumber}
                includeOther
                required
                label={intl.formatMessage({ id: "general.scacCode", defaultMessage: "SCAC Code" })}
                placeholder={intl.formatMessage({
                  id: "tracking.scacCodePlaceholder",
                  defaultMessage: "i.e. ABCD",
                })}
                testId="add-tracking-number-mbol-scac-select"
              />
              {selectedScacNumber === SCAC_OTHER_VALUE && (
                <div className="mt-3">
                  <Input
                    label={intl.formatMessage({ id: "general.scacCode", defaultMessage: "SCAC Code" })}
                    value={scacOtherCode}
                    onChange={(event) =>
                      setScacOtherCode(event.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4))
                    }
                    placeholder={intl.formatMessage({
                      id: "tracking.scacCodePlaceholder",
                      defaultMessage: "i.e. ABCD",
                    })}
                    testId="add-tracking-number-mbol-scac-other-input"
                  />
                </div>
              )}
            </>
          )}

          {selectedType === "container_no" && (
            <>
              <TrackingScacSelect
                scacCodes={scacCodes}
                value={selectedScacNumber}
                onChange={setSelectedScacNumber}
                isClearable
                testId="add-tracking-number-container-scac-select"
              />
              <div className="mt-3">
                <Input
                  label={intl.formatMessage({
                    id: "tracking.containerBolLabel",
                    defaultMessage: "BL / Booking number",
                  })}
                  value={containerBol}
                  onChange={(event) => setContainerBol(event.target.value)}
                  testId="add-tracking-number-container-bol-input"
                />
              </div>
            </>
          )}

          {unsupportedLiveTrackingTypes.has(selectedType) && (
            <div className="d-flex text-sm text-tertiary mt-4" style={{ maxWidth: 250 }}>
              <InfoIcon className="mr-2" width={20} height={20} />
              <div>
                <FormattedMessage
                  id="tracking.doNotSupportThisReferenceType"
                  defaultMessage="We currently do not support live tracking of this reference type."
                />
              </div>
            </div>
          )}

          {selectedType === "truck" && company_shipper_settings?.truck_tracking_enabled && (
            <div className="text-warning-primary font-b3 mt-md" style={{ maxWidth: 250 }}>
              <FormattedMessage
                id="tracking.truckHelpMessage"
                defaultMessage="We will generate a link for you to send to the truck driver to track their location."
              />
            </div>
          )}

        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end">
          <div className="mr-lg">
            <Button
              variant="secondary-gray"
              label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
              onClick={close}
            />
          </div>
          <Button
            label={intl.formatMessage({ id: "general.confirm", defaultMessage: "Confirm" })}
            onClick={onSave}
            disabled={isButtonDisabled() || isSubmitting}
            testId={testIds.SHIPMENT_DASHBOARD_TRACKING_REFERENCE_SUBMIT_BUTTON}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTrackingNumberModal;
